// Takvim veri katmanı (TanStack Query) — Blazor karşılığı: HomePage.razor.cs takvim bölümü
// (LoadCalendarSources / LoadMergedEvents / ToggleSource / SyncSource).
//
// NOT: DAL'ın `GET api/calendar/events/{userId}` ucunda tarih aralığı parametresi YOKTUR ve her
// çağrıda dış kaynakları (iCal/Google/Outlook) bayatsa senkronlar. Bu yüzden sorgu anahtarına
// görünür aralık KONULMAZ (her ay değişiminde gereksiz senkron tetiklerdi); anahtar kullanıcı +
// etkin kaynak imzasıdır, görünür aralık süzmesi istemcide yapılır.

import { useCallback, useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/entities/session/model/authStore';
import { toast } from '@/shared/ui/toastStore';
import { calendarApi } from '../api/calendarApi';
import { eventsInRange, normalizeEvents, visibleRange } from '../model/calendarModel';
import type {
  CalendarEvent,
  CalendarSource,
  CalendarSourceCreate,
  CalendarViewKind,
  DateRange,
  LocalCalendarEventInput,
} from '../model/calendarTypes';

export const calendarKeys = {
  all: ['calendar'] as const,
  sources: (userId: string) => ['calendar', 'sources', userId] as const,
  events: (userId: string, sourcesKey: string) => ['calendar', 'events', userId, sourcesKey] as const,
};

/** Etkin kaynakların imzası — birleşik uç yalnızca etkin kaynakları döndürür. */
function enabledSourcesKey(sources: CalendarSource[] | undefined): string {
  if (!sources?.length) return '';
  return sources
    .filter((s) => s.isEnabled)
    .map((s) => s.calendarSourceId)
    .sort()
    .join(',');
}

export interface UseCalendarArgs {
  /** Görünümün odaklandığı tarih. */
  anchor: Date;
  view: CalendarViewKind;
  agendaDays?: number;
  /** false ise hiçbir sorgu çalışmaz (kapalı widget vb.). */
  enabled?: boolean;
}

export interface UseCalendarResult {
  userId: string;
  sources: CalendarSource[];
  /** Tüm normalize etkinlikler. */
  events: CalendarEvent[];
  /** Görünür aralıkla kesişenler. */
  visibleEvents: CalendarEvent[];
  range: DateRange;
  isLoading: boolean;
  isFetching: boolean;
  error: unknown;
  refresh: () => void;
  toggleSource: (sourceId: string) => void;
  deleteSource: (sourceId: string) => void;
  syncSource: (sourceId: string) => void;
  /** Şu an senkronlanan kaynak. */
  syncingSourceId: string | null;
  createSource: (source: CalendarSourceCreate) => Promise<CalendarSource>;
  isCreatingSource: boolean;
  saveEvent: (event: LocalCalendarEventInput) => Promise<void>;
  deleteEvent: (eventId: string) => Promise<void>;
  isSavingEvent: boolean;
}

export function useCalendar(args: UseCalendarArgs): UseCalendarResult {
  const { view, agendaDays = 30, enabled = true } = args;
  const anchorTime = args.anchor.getTime();
  const userId = useAuthStore((s) => s.user?.userId ?? '');
  const qc = useQueryClient();
  const active = enabled && !!userId;

  const sourcesQuery = useQuery({
    queryKey: calendarKeys.sources(userId),
    queryFn: () => calendarApi.getSources(userId),
    enabled: active,
    staleTime: 5 * 60_000,
  });

  const sources = useMemo(
    () => [...(sourcesQuery.data ?? [])].sort((a, b) => a.sortOrder - b.sortOrder),
    [sourcesQuery.data],
  );
  const sourcesKey = enabledSourcesKey(sourcesQuery.data);

  const eventsQuery = useQuery({
    queryKey: calendarKeys.events(userId, sourcesKey),
    queryFn: () => calendarApi.getMergedEvents(userId),
    // Kaynaklar gelmeden çalıştırılmaz; yoksa anahtar bir kez değişip çift istek olurdu.
    enabled: active && sourcesQuery.isSuccess,
    staleTime: 60_000,
  });

  // İlk kullanımda kullanıcının hiç kaynağı yoktur; birleşik uç varsayılan "Octo Takvimim"
  // kaydını kendisi açar, bu yüzden kaynak listesi bir kez tazelenir.
  const sourcesEmpty = sourcesQuery.isSuccess && sources.length === 0;
  const eventsLoaded = eventsQuery.isSuccess;
  useEffect(() => {
    if (!active || !sourcesEmpty || !eventsLoaded) return;
    void qc.invalidateQueries({ queryKey: calendarKeys.sources(userId) });
  }, [active, sourcesEmpty, eventsLoaded, qc, userId]);

  const events = useMemo(
    () => normalizeEvents(eventsQuery.data?.events, sources),
    [eventsQuery.data, sources],
  );

  const range = useMemo(
    () => visibleRange(new Date(anchorTime), view, { agendaDays }),
    [anchorTime, view, agendaDays],
  );
  const visibleEvents = useMemo(() => eventsInRange(events, range), [events, range]);

  const invalidateAll = useCallback(() => {
    void qc.invalidateQueries({ queryKey: calendarKeys.sources(userId) });
    void qc.invalidateQueries({ queryKey: ['calendar', 'events', userId] });
  }, [qc, userId]);

  // ── Kaynak işlemleri ──
  const toggleMutation = useMutation({
    mutationFn: (sourceId: string) => calendarApi.toggleSource(sourceId),
    onMutate: async (sourceId: string) => {
      await qc.cancelQueries({ queryKey: calendarKeys.sources(userId) });
      const previous = qc.getQueryData<CalendarSource[]>(calendarKeys.sources(userId));
      qc.setQueryData<CalendarSource[]>(calendarKeys.sources(userId), (old) =>
        (old ?? []).map((s) => (s.calendarSourceId === sourceId ? { ...s, isEnabled: !s.isEnabled } : s)),
      );
      return { previous };
    },
    onError: (err, _sourceId, context) => {
      if (context?.previous) qc.setQueryData(calendarKeys.sources(userId), context.previous);
      toast.error(messageOf(err));
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: calendarKeys.sources(userId) });
    },
  });

  const deleteSourceMutation = useMutation({
    mutationFn: (sourceId: string) => calendarApi.deleteSource(sourceId, userId),
    onSuccess: invalidateAll,
    onError: (err) => toast.error(messageOf(err)),
  });

  const syncMutation = useMutation({
    mutationFn: (sourceId: string) => calendarApi.syncSource(sourceId),
    onSuccess: (result) => {
      if (result?.success === false) toast.error(result.error ?? 'Senkronizasyon başarısız.');
      invalidateAll();
    },
    onError: (err) => toast.error(messageOf(err)),
  });

  const createSourceMutation = useMutation({
    mutationFn: (source: CalendarSourceCreate) => calendarApi.createSource(source),
    onSuccess: invalidateAll,
    onError: (err) => toast.error(messageOf(err)),
  });

  // ── Yerel etkinlik işlemleri ──
  const saveEventMutation = useMutation({
    mutationFn: async (event: LocalCalendarEventInput): Promise<void> => {
      if (event.calendarEventId) await calendarApi.updateLocalEvent(event);
      else await calendarApi.createLocalEvent(event);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['calendar', 'events', userId] });
    },
    onError: (err) => toast.error(messageOf(err)),
  });

  const deleteEventMutation = useMutation({
    mutationFn: (eventId: string) => calendarApi.deleteLocalEvent(eventId, userId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['calendar', 'events', userId] });
    },
    onError: (err) => toast.error(messageOf(err)),
  });

  const toggleSource = useCallback((id: string) => toggleMutation.mutate(id), [toggleMutation]);
  const deleteSource = useCallback((id: string) => deleteSourceMutation.mutate(id), [deleteSourceMutation]);
  const syncSource = useCallback((id: string) => syncMutation.mutate(id), [syncMutation]);
  const createSource = useCallback(
    (s: CalendarSourceCreate) => createSourceMutation.mutateAsync(s),
    [createSourceMutation],
  );
  const saveEvent = useCallback(
    async (e: LocalCalendarEventInput) => {
      await saveEventMutation.mutateAsync(e);
    },
    [saveEventMutation],
  );
  const deleteEvent = useCallback(
    async (id: string) => {
      await deleteEventMutation.mutateAsync(id);
    },
    [deleteEventMutation],
  );

  return {
    userId,
    sources,
    events,
    visibleEvents,
    range,
    isLoading: sourcesQuery.isLoading || eventsQuery.isLoading,
    isFetching: sourcesQuery.isFetching || eventsQuery.isFetching,
    error: eventsQuery.error ?? sourcesQuery.error,
    refresh: invalidateAll,
    toggleSource,
    deleteSource,
    syncSource,
    syncingSourceId: syncMutation.isPending ? (syncMutation.variables ?? null) : null,
    createSource,
    isCreatingSource: createSourceMutation.isPending,
    saveEvent,
    deleteEvent,
    isSavingEvent: saveEventMutation.isPending || deleteEventMutation.isPending,
  };
}

function messageOf(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err)
    return String((err as { message?: string }).message ?? 'İşlem başarısız.');
  return 'İşlem başarısız.';
}
